import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import FleetMap from "@/components/FleetMap";
import { fmtTime, fromNow, isStalePing, fmtMiles } from "@/lib/constants";
import { Skeleton } from "@/components/ui/skeleton";
import { Truck, MapPin, Navigation, Radio, Clock, ChevronRight, RadioTower } from "lucide-react";

export default function FleetMapPage() {
  const navigate = useNavigate();
  const [trucks, setTrucks] = useState(null);

  const load = useCallback(() => {
    api.get("/fleet/live").then(({ data }) => setTrucks(data)).catch(() => setTrucks([]));
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, 8000);
    return () => clearInterval(iv);
  }, [load]);

  return (
    <div className="mx-auto max-w-6xl">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-stark-terracotta text-white"><RadioTower className="h-6 w-6" /></div>
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-stark-ink sm:text-3xl">Live Fleet Map</h1>
          <p className="text-sm text-stark-muted">{trucks ? `${trucks.length} team${trucks.length === 1 ? "" : "s"} on the road` : "Loading…"}</p>
        </div>
        <div className="ml-auto flex items-center gap-2 rounded-full bg-white px-3.5 py-1.5 text-xs font-semibold text-stark-muted shadow-sm">
          <span className="h-2 w-2 animate-pulse rounded-full bg-stark-success" /> Auto-refresh 8s
        </div>
      </div>

      {!trucks ? (
        <Skeleton className="mt-6 h-[520px] rounded-2xl" />
      ) : trucks.length === 0 ? (
        <div className="stark-card mt-6 flex flex-col items-center justify-center py-24 text-center">
          <Navigation className="h-12 w-12 text-stark-border-strong" />
          <p className="mt-3 text-lg font-bold text-stark-ink">No teams en route</p>
          <p className="text-sm text-stark-muted">Trucks appear here live once a route is started.</p>
        </div>
      ) : (
        <div className="mt-6 grid gap-5 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <FleetMap trucks={trucks} height={640} onSelect={(t) => navigate(`/jobs/${t.id}`)} />
          </div>
          <div className="stark-scroll max-h-[640px] space-y-3 overflow-auto">
            {trucks.map((t) => {
              const stale = isStalePing(t.last_ping_at);
              return (
              <button
                key={t.id}
                onClick={() => navigate(`/jobs/${t.id}`)}
                data-testid={`fleet-item-${t.id}`}
                className="stark-card group flex w-full items-center gap-3 p-4 text-left transition-all hover:-translate-y-0.5"
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-stark-tint text-stark-terracotta">
                  <Truck className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate font-bold text-stark-ink">{t.customer_name}</div>
                  <div className="flex items-center gap-1 truncate text-xs text-stark-muted"><MapPin className="h-3 w-3" /> {t.address}</div>
                  <div className="mt-1 flex items-center gap-3 text-[11px] font-semibold">
                    {t.current_lat != null && !stale ? (
                      <span className="text-stark-terracotta">ETA ~{Math.round(t.eta_minutes)} min{t.distance_km != null ? ` · ${fmtMiles(t.distance_km)}` : ""}</span>
                    ) : t.current_lat != null && stale ? (
                      <span className="text-stark-warning">Location delayed</span>
                    ) : (
                      <span className="flex items-center gap-1 text-stark-muted"><Radio className="h-3 w-3 animate-pulse" /> Locating…</span>
                    )}
                    {t.last_ping_at && <span className="text-stark-muted"><Clock className="mr-0.5 inline h-3 w-3" />{fromNow(t.last_ping_at)}</span>}
                  </div>
                  {t.driver_name && <div className="mt-0.5 text-[11px] text-stark-muted">{t.driver_name}{t.truck_name ? ` · ${t.truck_name}` : ""}</div>}
                </div>
                <ChevronRight className="h-4 w-4 shrink-0 text-stark-border-strong" />
              </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
